import { useEffect, useRef, useState } from "react";
import { Bold, Briefcase, File as FileIcon, Image as ImageIcon, Italic, List, MapPin, Paperclip, Send, Smile, Sticker, Store, Strikethrough, User, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { VoiceRecorder } from "@/components/chat/VoiceRecorder";

const EMOJIS = "😀 😁 😂 🙂 😉 😊 😍 🤩 😎 🤔 😅 😇 🙏 👍 👎 👏 🙌 💪 🤝 👌 ✌️ 🔥 💯 ✅ ❌ ⭐ 🎉 🎊 ❤️ 💚 💼 📈 📦 🚚 ✈️ 🏗️ 💡 📌 📎 📞 ⏰ 🇨🇮".split(" ");

function Picker({ kind, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [sup, setSup] = useState({ name: "", phone: "", note: "" });
  useEffect(() => {
    if (kind === "contact") api.get("/members", { params: { q } }).then(({ data }) => setItems(data.items)).catch(() => {});
    if (kind === "operation") api.get("/operations").then(({ data }) => setItems(data.filter((o) => o.title.toLowerCase().includes(q.toLowerCase())))).catch(() => {});
  }, [kind, q]);
  if (kind === "fournisseur") {
    return (
      <div className="space-y-2">
        <input data-testid="supplier-name-input" className="mr-input" placeholder="Nom du fournisseur" value={sup.name} onChange={(e) => setSup({ ...sup, name: e.target.value })} />
        <input data-testid="supplier-phone-input" className="mr-input" placeholder="Téléphone" value={sup.phone} onChange={(e) => setSup({ ...sup, phone: e.target.value })} />
        <input className="mr-input" placeholder="Note (produits, ville…)" value={sup.note} onChange={(e) => setSup({ ...sup, note: e.target.value })} />
        <button data-testid="supplier-send-button" className="mr-btn-primary w-full" disabled={sup.name.trim().length < 2} onClick={() => onPick(sup)}>Partager la carte</button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <input data-testid="picker-search-input" className="mr-input" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="max-h-72 overflow-y-auto divide-y divide-mr-border">
        {items.map((it) => (
          <li key={it.id}><button data-testid="picker-item" className="w-full text-left py-2.5 text-sm hover:text-mr-accent" onClick={() => onPick(it)}>
            {it.name || it.title}<span className="text-mr-muted"> {it.company || ""}</span></button></li>
        ))}
      </ul>
      <button className="mr-btn-ghost w-full" onClick={onClose}>Fermer</button>
    </div>
  );
}

export function Composer({ conv, onSend, replyTo, editing, onCancel, stickers, voiceMax }) {
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [picker, setPicker] = useState(null);
  const [pop, setPop] = useState(null);
  const [busy, setBusy] = useState(false);
  const ta = useRef(null);
  const fileIn = useRef(null);
  const docIn = useRef(null);
  const lastTyping = useRef(0);

  useEffect(() => {
    if (editing) setText(editing.content);
  }, [editing]);

  const wrap = (mark) => {
    const el = ta.current;
    const { selectionStart: a, selectionEnd: b } = el;
    const sel = text.slice(a, b) || "texte";
    setText(text.slice(0, a) + mark + sel + mark + text.slice(b));
    el.focus();
  };
  const listify = () => setText((t) => t.split("\n").map((l) => (l.startsWith("- ") ? l : `- ${l}`)).join("\n"));

  const change = (v) => {
    setText(v);
    const m = /@([A-Za-zÀ-ÿ'-]*)$/.exec(v.slice(0, ta.current?.selectionStart ?? v.length));
    setSuggest(m && conv.type !== "direct" ? m[1].toLowerCase() : null);
    if (Date.now() - lastTyping.current > 2500) {
      lastTyping.current = Date.now();
      window.mrWsSend?.({ type: "typing", conversation_id: conv.id });
    }
  };
  const pickMention = (p) => {
    setText((t) => t.replace(/@([A-Za-zÀ-ÿ'-]*)$/, `@${p.name} `));
    setMentions((ms) => [...new Set([...ms, p.id])]);
    setSuggest(null);
    ta.current?.focus();
  };

  const submit = async (payload) => {
    setBusy(true);
    try {
      await onSend(payload);
      setText("");
      setMentions([]);
    } finally {
      setBusy(false);
    }
  };
  const sendText = (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    submit({ kind: "texte", content: text, mention_ids: mentions });
  };

  const sendFiles = async (files, kindOverride) => {
    for (const f of files) {
      const kind = kindOverride || (f.type.startsWith("video/") ? "video" : f.type.startsWith("image/") ? "image" : "document");
      try {
        setBusy(true);
        const id = await uploadFile(kind === "image" ? "chat_photo" : kind, f);
        await onSend({ kind, media_file_ids: [id], content: text, meta: { filename: f.name, size: f.size } });
        setText("");
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy(false);
      }
    }
  };

  const sendVoice = async (file, meta) => {
    try {
      setBusy(true);
      const id = await uploadFile("audio", file);
      await onSend({ kind: "vocal", media_file_ids: [id], meta });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const sendLocation = () => {
    if (!navigator.geolocation) return toast.error("Localisation indisponible.");
    navigator.geolocation.getCurrentPosition(
      (p) => submit({ kind: "localisation", meta: { lat: p.coords.latitude, lng: p.coords.longitude, label: "Ma position" } }),
      () => toast.error("Autorisez la localisation pour la partager."),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const onPicked = (it) => {
    const k = picker;
    setPicker(null);
    if (k === "contact") submit({ kind: "contact", meta: { user_id: it.id, name: it.name, company: it.company } });
    if (k === "operation") submit({ kind: "operation", meta: { operation_id: it.id, title: it.title, status: it.status } });
    if (k === "fournisseur") submit({ kind: "fournisseur", meta: it });
  };

  const people = (conv.participants || []).filter((p) => suggest !== null && p.name.toLowerCase().includes(suggest)).slice(0, 6);

  if (!conv.can_send) return <p className="text-center text-sm text-mr-muted py-4 border-t border-mr-border" data-testid="thread-readonly">Canal d'annonces : seuls les administrateurs publient.</p>;
  if (conv.blocked) return <p className="text-center text-sm text-mr-muted py-4 border-t border-mr-border" data-testid="thread-blocked">Vous ne pouvez plus échanger avec ce membre.</p>;

  return (
    <div className="sticky bottom-20 mt-4 bg-mr-bg pt-2 space-y-2" data-testid="composer">
      {(replyTo || editing) && (
        <div className="flex items-start gap-2 text-xs border-l-2 border-mr-accent bg-mr-surface px-3 py-2" data-testid="composer-context">
          <div className="flex-1 min-w-0"><p className="font-semibold">{editing ? "Modifier le message" : `Réponse à ${replyTo.sender_name}`}</p>
            <p className="text-mr-text2 truncate">{(editing || replyTo).content || "Pièce jointe"}</p></div>
          <button aria-label="Annuler" data-testid="composer-cancel-context" onClick={() => { onCancel(); setText(""); }}><X size={14} /></button>
        </div>
      )}
      {people.length > 0 && (
        <ul className="mr-card p-1" data-testid="mention-suggestions">
          {people.map((p) => <li key={p.id}><button data-testid="mention-option" className="w-full text-left px-3 py-2 text-sm hover:bg-mr-raised" onClick={() => pickMention(p)}>@{p.name}</button></li>)}
        </ul>
      )}
      <div className="flex items-center gap-0.5 text-mr-muted">
        {[[Bold, "*", "Gras"], [Italic, "_", "Italique"], [Strikethrough, "~", "Barré"]].map(([I, mk, l]) => (
          <button key={l} type="button" aria-label={l} data-testid={`format-${mk === "*" ? "bold" : mk === "_" ? "italic" : "strike"}`} onClick={() => wrap(mk)} className="w-8 h-8 grid place-items-center hover:text-mr-text"><I size={15} /></button>
        ))}
        <button type="button" aria-label="Liste" data-testid="format-list" onClick={listify} className="w-8 h-8 grid place-items-center hover:text-mr-text"><List size={15} /></button>
        <Popover>
          <PopoverTrigger data-testid="emoji-button" aria-label="Émojis" className="w-8 h-8 grid place-items-center hover:text-mr-text"><Smile size={16} /></PopoverTrigger>
          <PopoverContent className="w-72 grid grid-cols-8 gap-1 p-2">{EMOJIS.map((e) => <button key={e} className="text-xl hover:scale-125 transition-transform" onClick={() => setText((t) => t + e)}>{e}</button>)}</PopoverContent>
        </Popover>
        {!editing && (
          <>
            <Popover open={pop === "sticker"} onOpenChange={(o) => setPop(o ? "sticker" : null)}>
              <PopoverTrigger data-testid="sticker-button" aria-label="Stickers" className="w-8 h-8 grid place-items-center hover:text-mr-text"><Sticker size={16} /></PopoverTrigger>
              <PopoverContent className="w-80 p-2 max-h-80 overflow-y-auto">
                {stickers.map((p) => (
                  <div key={p.id} className="mb-2"><p className="mr-eyebrow mb-1">{p.name}</p>
                    <div className="grid grid-cols-4 gap-2">{p.stickers.map((s) => <button key={s} data-testid="sticker-option" onClick={() => { setPop(null); submit({ kind: "sticker", meta: { url: s } }); }}><img src={s} alt="" className="w-16 h-16" /></button>)}</div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <Popover open={pop === "attach"} onOpenChange={(o) => setPop(o ? "attach" : null)}>
              <PopoverTrigger data-testid="attach-button" aria-label="Joindre" className="w-8 h-8 grid place-items-center hover:text-mr-text"><Paperclip size={16} /></PopoverTrigger>
              <PopoverContent className="w-56 p-1">
                {[[ImageIcon, "Photo ou vidéo", () => fileIn.current.click(), "attach-media"], [FileIcon, "Document (PDF, Word, Excel)", () => docIn.current.click(), "attach-document"],
                  [MapPin, "Localisation", sendLocation, "attach-location"], [User, "Carte de contact", () => setPicker("contact"), "attach-contact"],
                  [Briefcase, "Carte d'opération", () => setPicker("operation"), "attach-operation"], [Store, "Carte fournisseur", () => setPicker("fournisseur"), "attach-supplier"]].map(([I, l, fn, id]) => (
                  <button key={id} data-testid={id} onClick={() => { setPop(null); fn(); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-mr-raised text-left"><I size={15} /> {l}</button>
                ))}
              </PopoverContent>
            </Popover>
          </>
        )}
        <input ref={fileIn} type="file" accept="image/*,video/*" multiple hidden data-testid="attach-media-input" onChange={(e) => { sendFiles([...e.target.files]); e.target.value = ""; }} />
        <input ref={docIn} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" hidden data-testid="attach-document-input" onChange={(e) => { sendFiles([...e.target.files], "document"); e.target.value = ""; }} />
      </div>
      <form onSubmit={sendText} className="flex items-end gap-2">
        <textarea ref={ta} rows={1} data-testid="thread-message-input" className="mr-input resize-none max-h-32 py-2.5" placeholder="Votre message…" value={text} maxLength={4000}
          onChange={(e) => change(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.innerWidth > 768) sendText(e); }} />
        {text.trim() || editing
          ? <button data-testid="thread-send-button" aria-label="Envoyer" className="mr-btn-primary px-4 h-11" disabled={busy || !text.trim()}><Send size={17} /></button>
          : <VoiceRecorder onDone={sendVoice} maxSeconds={voiceMax} />}
      </form>
      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="max-w-md">
          <DialogTitle>{picker === "contact" ? "Partager un contact" : picker === "operation" ? "Partager une opération" : "Carte fournisseur"}</DialogTitle>
          {picker && <Picker kind={picker} onPick={onPicked} onClose={() => setPicker(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
